import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import type { ModalProps } from '@/types/ui';
import { Button } from './Button';
import { useModalA11y } from '@/hooks/useModalA11y';

/**
 * Webcoda-styled Modal component
 * Accessible modal dialog with backdrop, animations, and keyboard handling
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closable = true,
  footer,
  centered = true,
  className,
  children,
  'data-testid': testId,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  };

  const titleId = useId();

  // The app root is safe to inert here because this modal portals to document.body.
  // Inline overlays must not do this - they would inert themselves.
  const appRootRef = useRef<HTMLElement | null>(null);
  if (typeof document !== 'undefined' && !appRootRef.current) {
    appRootRef.current = document.getElementById('root');
  }

  useModalA11y({ isOpen, onClose, containerRef: modalRef, closable, inertRef: appRootRef });

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closable) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex',
        centered ? 'items-center justify-center' : 'items-start justify-center pt-16',
        'p-4'
      )}
      onClick={handleBackdropClick}
      data-testid={testId}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={clsx(
          'relative w-full bg-white rounded-xl shadow-large transform transition-all',
          'animate-in fade-in-0 zoom-in-95 duration-200',
          sizeClasses[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        {...props}
      >
        {/* Header */}
        {(title || closable) && (
          <div className="flex items-center justify-between p-6 pb-4">
            {title && (
              <h2 
                id={titleId}
                className="text-lg font-semibold text-gray-900 leading-6"
              >
                {title}
              </h2>
            )}
            
            {closable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="!p-2 -mr-2"
                aria-label="Close modal"
              >
                <svg 
                  className="h-5 w-5" 
                  fill="none" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={clsx('px-6', title || closable ? 'pb-6' : 'py-6')}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Portal to body
  return createPortal(modalContent, document.body);
};

/**
 * Confirmation Modal - Pre-configured modal for confirmations
 */
export const ConfirmModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success' | 'secondary';
  loading?: boolean;
}> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to perform this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
}) => {
  const variantConfig = {
    danger: {
      confirmVariant: 'danger' as const,
      icon: (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-100">
          <svg className="h-6 w-6 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
      ),
    },
    warning: {
      confirmVariant: 'primary' as const,
      icon: (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-100">
          <svg className="h-6 w-6 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
      ),
    },
    info: {
      confirmVariant: 'primary' as const,
      icon: (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
          <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
    },
    success: {
      confirmVariant: 'primary' as const,
      icon: (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ),
    },
    secondary: {
      confirmVariant: 'outline' as const,
      icon: (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
    },
  };

  const config = variantConfig[variant] || variantConfig.danger; // Fallback to danger if variant is invalid

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant={config.confirmVariant}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <div className="flex items-start space-x-4">
        {config.icon}
        <div className="flex-1">
          <p className="text-sm text-gray-600 leading-relaxed">
            {message}
          </p>
        </div>
      </div>
    </Modal>
  );
};

// Attach variants to main Modal component
Object.assign(Modal, { Confirm: ConfirmModal });